import { format } from "date-fns";

/**
 * Shared utilities for claim status display and formatting
 * Used by ClaimDetailsDrawer and ClaimsTable components
 */

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format currency values for display
 * @param amount - Amount in dollars (not cents)
 * @param decimals - Number of decimal places (default: 0 for table, 2 for details)
 */
export const formatCurrency = (amount: number | null, decimals: number = 0): string => {
  if (amount === null || amount === undefined) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

/**
 * Format date strings for display
 * @param dateString - ISO date string
 * @returns Formatted date as MM/dd/yyyy or "N/A"
 */
export const formatDate = (dateString: string | null): string => {
  if (!dateString) return "N/A";
  try {
    return format(new Date(dateString), "MM/dd/yyyy");
  } catch {
    return "Invalid Date";
  }
};

// ============================================================================
// Status Display Logic
// ============================================================================

/**
 * Transform status text for display
 * Maps "Needs Review" to "Adjuster Review" for user clarity
 * @param status - Raw status from database
 * @returns User-friendly status text
 */
export const getStatusDisplayText = (status: string): string => {
  if (status?.toLowerCase() === "needs review") {
    return "Adjuster Review";
  }
  return status;
};

/**
 * Get custom background and text colors for status chips
 * Provides consistent color mapping across all claim status displays
 * @param status - Claim status string
 * @returns Object with backgroundColor and color (text color)
 */
export const getStatusChipColors = (status: string): { backgroundColor: string; color: string } => {
  const statusLower = status?.toLowerCase() || "";

  // Adjuster Review (Needs Review, Needs Approval, Pending Settlement, Adjuster Action): #DDE3FF
  if (statusLower.includes("needs review")) return { backgroundColor: "#DDE3FF", color: "#1a237e" };
  if (statusLower.includes("needs approval")) return { backgroundColor: "#DDE3FF", color: "#1a237e" };
  if (statusLower.includes("pending settlement")) return { backgroundColor: "#DDE3FF", color: "#1a237e" };
  if (statusLower.includes("adjuster action")) return { backgroundColor: "#DDE3FF", color: "#1a237e" };

  // Closed Settled: #F0FDFA
  if (statusLower.includes("settled")) return { backgroundColor: "#F0FDFA", color: "#004d40" };

  // Tenant Action Needed: #FFF3C6
  if (statusLower.includes("tenant action")) return { backgroundColor: "#FFF3C6", color: "#5d4e00" };

  // Facility Action Needed: #FEE4E2
  if (statusLower.includes("facility action")) return { backgroundColor: "#FEE4E2", color: "#7f1d1d" };

  // Grey: All other Closed statuses and Incomplete
  if (statusLower.includes("closed")) return { backgroundColor: "#e0e0e0", color: "#424242" };
  if (statusLower === "incomplete") return { backgroundColor: "#e0e0e0", color: "#424242" };

  // Blue: New Claim and other statuses
  if (statusLower.includes("new claim")) return { backgroundColor: "#e3f2fd", color: "#0d47a1" };

  // Default
  return { backgroundColor: "#e3f2fd", color: "#0d47a1" };
};
